'use client';

import { ApolloLink, from, Observable } from '@apollo/client';
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/client-integration-nextjs';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { toast } from 'react-toastify';

import FlashMessage from '~/shared/components/flash-message';
import { API_HOST, DEFAULT_LOCALE } from '~/shared/constants';
import { reportRumError } from '~/shared/helpers/graphql-error-helpers';
import { gqlLoggerLink } from '~/shared/libs/apollo/logging';
import i18next from '~/shared/libs/i18n/client-side';
import { createClientRequestId } from '~/shared/libs/observability/client-request-id';

const middleware = new ApolloLink((operation, forward) => {
  return forward ? forward(operation) : null;
});

const ensureErrorNamespaceLoaded = async () => {
  const errorNamespace = 'error';

  if (!i18next.hasLoadedNamespace(errorNamespace)) {
    await i18next.loadNamespaces([errorNamespace]);
  }

  return errorNamespace;
};

const afterware = new ApolloLink((operation, forward) => {
  if (!forward) {
    return null;
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (response) => {
        const message = response?.data?.[operation.operationName]?.message ?? '';
        const title = response?.data?.[operation.operationName]?.title ?? '';

        if (message && typeof window !== 'undefined') {
          toast.success(<FlashMessage title={title} message={message} />);
        }

        observer.next(response);
      },
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    });

    return () => subscription.unsubscribe();
  });
});

const clientRequestIdLink = setContext((_, previousContext) => {
  const clientRequestId = typeof window !== 'undefined' ? createClientRequestId() : undefined;

  return {
    ...previousContext,
    headers: {
      ...(previousContext.headers || {}),
      ...(clientRequestId ? { 'x-client-request-id': clientRequestId } : {}),
    },
    clientRequestId,
  };
});
const isProduction = process.env.NODE_ENV === 'production';

const shouldShowTechnicalError = !isProduction;
const errorLink = onError(({ error, operation }) => {
  const clientRequestId = operation.getContext()?.clientRequestId as string | undefined;

  if (CombinedGraphQLErrors.is(error)) {
    const normalizedErrors = error.errors.map((item) => {
      const rawMessage = item.message || '';
      const rawTitle =
        (item?.title as string | undefined) ||
        (item.extensions?.title as string | undefined) ||
        ((item.extensions?.exception as { title?: string } | undefined)?.title ?? '');

      const isSystemGraphQLError =
        !rawTitle &&
        (rawMessage.includes('Illegal arguments:') ||
          rawMessage.includes('Variable "$') ||
          rawMessage.includes('got invalid value') ||
          rawMessage.includes('Field "') ||
          rawMessage.includes('was not provided') ||
          rawMessage.includes('GraphQL validation') ||
          rawMessage.includes('Syntax Error') ||
          rawMessage.includes('Cannot query field') ||
          rawMessage.includes('Unknown argument') ||
          rawMessage.includes('Expected type'));

      return {
        rawMessage,
        rawTitle,
        code: typeof item.extensions?.code === 'string' ? item.extensions.code : '',
        path: Array.isArray(item.path) ? item.path.join('.') : '',
        isSystemGraphQLError,
      };
    });

    for (const item of normalizedErrors) {
      reportRumError({
        source: 'apollo',
        operationName: operation.operationName,
        message: item.rawMessage || 'Unknown GraphQL error',
        clientRequestId,
        variables: operation.variables,
        extra: {
          graphQLErrorPath: item.path,
          graphQLErrorCode: item.code,
          rawTitle: item.rawTitle,
          isSystemGraphQLError: item.isSystemGraphQLError,
        },
      });
    }

    if (typeof window !== 'undefined') {
      void (async () => {
        try {
          const namespace = await ensureErrorNamespaceLoaded();
          const firstHumanError = normalizedErrors.find((item) => !item.isSystemGraphQLError);
          const displayError = firstHumanError ?? normalizedErrors[0];

          const title = displayError?.isSystemGraphQLError
            ? i18next.t('error.message.graphql-error.title', {
                ns: namespace,
                defaultValue: 'Error GraphQL',
              })
            : displayError?.rawTitle ||
              i18next.t('error.message.unknown-error.title', {
                ns: namespace,
                defaultValue: 'Unknown Error',
              });

          const message = displayError?.isSystemGraphQLError
            ? i18next.t('error.message.graphql-error.message', {
                ns: namespace,
                defaultValue: 'Error GraphQL',
              })
            : displayError?.rawMessage ||
              i18next.t('error.message.unknown-error.message', {
                ns: namespace,
                defaultValue: 'Unknown Error occurs',
              });

          if (shouldShowTechnicalError || !displayError?.isSystemGraphQLError) {
            toast.error(<FlashMessage title={title} message={message} />);
          }
        } catch {
          toast.error(<FlashMessage title="Error" message="Request Error" />);
        }
      })();
    }

    return;
  }

  if (CombinedProtocolErrors.is(error)) {
    reportRumError({
      source: 'apollo',
      operationName: operation.operationName,
      message: 'Protocol error',
      clientRequestId,
      variables: operation.variables,
      extra: {
        protocolErrors: JSON.stringify(error.errors),
      },
    });

    if (typeof window !== 'undefined') {
      void (async () => {
        try {
          const namespace = await ensureErrorNamespaceLoaded();
          if (shouldShowTechnicalError) {
            toast.error(
              <FlashMessage
                title={i18next.t('error.message.graphql-error.title', {
                  ns: namespace,
                  defaultValue: 'Error GraphQL',
                })}
                message={i18next.t('error.message.graphql-error.message', {
                  ns: namespace,
                  defaultValue: 'Error GraphQL',
                })}
              />,
            );
          }
        } catch {
          toast.error(<FlashMessage title="Error" message="Request Error" />);
        }
      })();
    }

    return;
  }

  reportRumError({
    source: 'apollo',
    operationName: operation.operationName,
    message: error instanceof Error ? error.message : 'Unknown network error',
    clientRequestId,
    variables: operation.variables,
    extra: {
      networkError: true,
    },
  });

  if (typeof window !== 'undefined') {
    void (async () => {
      try {
        const namespace = await ensureErrorNamespaceLoaded();
        if (shouldShowTechnicalError) {
          toast.error(
            <FlashMessage
              title={i18next.t('error.message.network.title', {
                ns: namespace,
                defaultValue: 'Network Error',
              })}
              message={i18next.t('error.message.unknown-error.message', {
                ns: namespace,
                defaultValue: 'Unknown Error occurs',
              })}
            />,
          );
        }
      } catch {
        toast.error(<FlashMessage title="Network Error" message="Request Error" />);
      }
    })();
  }
});

const buildGraphqlUri = () => {
  if (typeof window !== 'undefined') {
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (process.env.NEXT_PUBLIC_LOCAL_API === 'true' || isLocalHost) {
      return `${window.location.protocol}//${window.location.hostname}:3002/api/graphql`;
    }

    return '/api/graphql';
  }

  const publicAppUrl = process.env.NEXT_PUBLIC_APP_URL?.replace(/\/+$/, '') || API_HOST;

  return `${publicAppUrl}/api/graphql`;
};

const createUploadFetch: typeof fetch = async (input, init) => {
  const headers = new Headers(init?.headers);

  headers.set('Apollo-Require-Preflight', 'true');

  if (typeof window === 'undefined') {
    headers.set('Accept-Language', DEFAULT_LOCALE);
  } else {
    headers.set('Accept-Language', localStorage?.getItem('locale') ?? DEFAULT_LOCALE);
  }

  return fetch(input, {
    ...init,
    credentials: 'include',
    headers,
  });
};

function makeApolloLink() {
  const baseLinks = [
    middleware,
    afterware,
    removeTypenameFromVariables(),
    ...(process.env.IS_APOLLO_LOGGER === 'true' ? [gqlLoggerLink] : []),
    ...(typeof window === 'undefined' ? [new SSRMultipartLink({ stripDefer: true })] : []),
    clientRequestIdLink,
    errorLink,
    new UploadHttpLink({
      uri: buildGraphqlUri(),
      credentials: 'include',
      fetch: createUploadFetch,
    }),
  ];

  return from(baseLinks);
}

function createApollo(initialState: Record<string, unknown> | null) {
  return new ApolloClient({
    cache: new InMemoryCache().restore(initialState ?? {}),
    devtools: {
      enabled: process.env.NODE_ENV !== 'production',
    },
    link: makeApolloLink(),
  });
}

let apolloClientCSR: ApolloClient<unknown> | null = null;

export function initializeApollo(initialState: Record<string, unknown> | null = null) {
  if (typeof window === 'undefined') {
    return createApollo(initialState);
  }

  if (!apolloClientCSR) {
    apolloClientCSR = createApollo(initialState);
  }

  return apolloClientCSR;
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return <ApolloNextAppProvider makeClient={() => initializeApollo(null)}>{children}</ApolloNextAppProvider>;
}
